/* © Facilogi. See COPYRIGHT file for full copyright & licensing details. */

import Feathers from "@feathersjs/feathers";

import { Customer, Invoice, PaymentMethod } from "App/Models";
import { AnnotatedJSON, HandleError, HandleListError } from "@Internal/Contracts";
import { Models } from "@Internal";
import { CustomError } from "@Internal/Models";
import { Autobind } from "@Internal/Utils";
import {
    CustomersService, GroupCompany, GroupUser, GroupUsersPage, GroupOverview
} from "App/Contracts";
import { CountryCode, GetCountryByCode } from "App/Utils";

import BaseService, { ProcessException } from "./Base";

export default class Customers extends BaseService<Customer> implements CustomersService {
    private invoicesService: Feathers.Service<any>;
    private groupUsersService: Feathers.Service<any>;
    private groupOverviewService: Feathers.Service<any>;
    private usersService: Feathers.Service<any>;
    private paymentMethodService: Feathers.Service<any>;
    private companiesService: Feathers.Service<any>;

    constructor(
        service: Feathers.Service<any>,
        invoicesService: Feathers.Service<any>,
        groupUsersService: Feathers.Service<any>,
        groupOverviewService: Feathers.Service<any>,
        usersService: Feathers.Service<any>,
        paymentMethodService: Feathers.Service<any>,
        companiesService: Feathers.Service<any>
    ) {
        super( service );

        this.invoicesService = invoicesService;
        this.groupUsersService = groupUsersService;
        this.groupOverviewService = groupOverviewService;
        this.usersService = usersService;
        this.paymentMethodService = paymentMethodService;
        this.companiesService = companiesService;
    }

    /* La societe de chaque groupe demande, en un appel ( `Customers/ByGroups` ). */
    @Autobind
    async CompaniesByGroups( groups: number[] ): HandleListError<GroupCompany> {
        if( groups.length === 0 )
            return [ [], undefined ];

        try {
            const results = await this.companiesService.find( {
                query: { groups: groups.join( "," ) }
            } );

            return [ ( results.Companies || [] ) as GroupCompany[], undefined ];

        } catch( exception ) {
            return [ [], await ProcessException( exception ) ];
        }
    }

    /* List desktop users across every account, filtered and paginated server-side. Powers the
       global Users page reachable from the sidebar. */
    @Autobind
    async ListUsers(
        page: number,
        limit: number,
        status: string,
        keyword: string
    ): Promise<[ GroupUsersPage, CustomError | undefined ]> {
        try {
            const results = await this.usersService.find({
                query: { page, limit, status, keyword: keyword.trim() }
            });

            const users = ( results.Users || [] ) as GroupUser[];
            const total = results.Total !== undefined ? Number( results.Total ) : users.length;

            return [ { Users: users, Total: total }, undefined ];

        } catch( exception ) {
            return [ { Users: [], Total: 0 }, await ProcessException( exception ) ];
        }
    }

    /* List every user of the desktop group linked to the customer account (current group members),
       so the customer's group users page can display and filter them. */
    @Autobind
    async ListGroupUsers( id: number ): HandleListError<GroupUser> {
        try {
            const results = await this.groupUsersService.get( id );

            if( results.Users === undefined )
                return [ [], undefined ];

            return [ results.Users as GroupUser[], undefined ];

        } catch( exception ) {
            return [ [], await ProcessException( exception ) ];
        }
    }

    /* Full overview of a group (group + linked customer + owner + member count + members), keyed by
       groupId. Powers the linked-group panel on the "Groupes et portails" page. */
    @Autobind
    async GetGroupOverview(
        groupId: number
    ): Promise<[ GroupOverview | undefined, CustomError | undefined ]> {
        try {
            const results = await this.groupOverviewService.get( groupId );

            return [ results as GroupOverview, undefined ];

        } catch( exception ) {
            return [ undefined, await ProcessException( exception ) ];
        }
    }

    /* Get the customer's active saved payment method (card), so the Payments tab can show it at
       the top. Returns undefined when the customer has no saved method. */
    @Autobind
    async GetPaymentMethod(
        id: number
    ): Promise<[ PaymentMethod | undefined, CustomError | undefined ]> {
        try {
            const results = await this.paymentMethodService.get( id );

            if( results.PaymentMethod === undefined || results.PaymentMethod === null )
                return [ undefined, undefined ];

            return [ results.PaymentMethod as PaymentMethod, undefined ];

        } catch( exception ) {
            return [ undefined, await ProcessException( exception ) ];
        }
    }

    /* List every invoice of the customer across all of his subscriptions (current and previously
       canceled ones), so the customer detail page can show the full billing history. */
    @Autobind
    async ListInvoices( id: number ): HandleListError<Invoice> {
        try {
            const results = await this.invoicesService.get( id );

            if( results.Invoices === undefined )
                return [ [], undefined ];

            const [ invoices, error ] =
                Models.Deserialize<AnnotatedJSON>( results.Invoices as AnnotatedJSON );

            if( error !== undefined || invoices === undefined )
                return [ [], error ];

            return [ Object.values( invoices ) as Invoice[], undefined ];

        } catch( exception ) {
            return [ [], await ProcessException( exception ) ];
        }
    }

    @Autobind
    async EndTrial( record: Customer ): Promise<CustomError | undefined> {
        try {
            const results = await this.service.update( record.Id, {
                EndTrial: true
            });

            if( results.Error !== undefined )
                return new CustomError( results.Error.Id, $t( results.Error.Message ) );

        } catch( error ) {
            return ProcessException( error );
        }
        return;
    }

    @Autobind
    async FindByNameOrEmail( keyword: string ): HandleListError<Customer> {
        let results: any;

        try {
            results = await this.service.find({
                query: {
                    keyword
                }
            });

        } catch( error ) {
            return [ [], await ProcessException( error ) ];
        }

        if( results.Error !== undefined )
            return [ [], new CustomError( results.Error.Id, results.Error.Message ) ];

        const customers: Customer[] = [];

        for( const record of results.Items ) {
            const [ customer, error ] = this.deserialize( record );

            if( error !== undefined || customer === undefined )
                return [ [], error ];

            customers.push( customer );
        }

        return [ customers, undefined ];
    }

    protected deserialize( result: AnnotatedJSON ): HandleError<Customer> {
        const [ customer, error ] = Models.Deserialize<Customer>( result );

        if( error !== undefined || customer === undefined )
            return [ undefined, error ];

        /* Get a country object by the country code provided by the server. */
        if( customer.Country !== undefined )
            customer.Country = GetCountryByCode( customer.Country as unknown as CountryCode );

        return [ customer, undefined ];
    }
}
